
import { createClient } from '@supabase/supabase-js';

// --- ROBUST ENVIRONMENT VARIABLE HANDLING ---
// The application needs to connect to Supabase.
// Vite uses `import.meta.env.VITE_...`
// Next.js / Vercel often uses `process.env.NEXT_PUBLIC_...`
// Standard Node/Vercel Backend might use `process.env...`

// 1. Try Vite
const viteUrl = import.meta.env.VITE_SUPABASE_URL;
const viteKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// 2. Try Standard/Next.js (if mixed environment)
const nextUrl = import.meta.env.NEXT_PUBLIC_SUPABASE_URL;
const nextKey = import.meta.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

// 3. Fallback Hardcoded (For this specific project to solve deployment issues)
const FALLBACK_URL = 'https://zvpxmxcznimhwhxjnhvn.supabase.co';
// Note: Anon key is public-safe, but ideally should be in env.
// If absolutely missing, the app will fail auth, but at least won't crash on init.
const FALLBACK_KEY = '';

export const supabaseUrl = viteUrl || nextUrl || FALLBACK_URL;
const supabaseAnonKey = viteKey || nextKey || FALLBACK_KEY;

let client;

// Mock query builder to support chaining: .select().eq().order().limit().maybeSingle()...
const createMockQueryBuilder = () => {
    const builder = {
        select: () => builder,
        insert: () => builder,
        update: () => builder,
        delete: () => builder,
        upsert: () => builder,
        eq: () => builder,
        neq: () => builder,
        gt: () => builder,
        lt: () => builder,
        gte: () => builder,
        lte: () => builder,
        like: () => builder,
        ilike: () => builder,
        is: () => builder,
        in: () => builder,
        contains: () => builder,
        containedBy: () => builder,
        range: () => builder,
        rangeAdjacent: () => builder,
        overlaps: () => builder,
        textSearch: () => builder,
        match: () => builder,
        not: () => builder,
        or: () => builder,
        filter: () => builder,
        order: () => builder,
        limit: () => builder,
        offset: () => builder,
        single: () => Promise.resolve({ data: null, error: { message: "Mock client: Supabase key missing" } }),
        maybeSingle: () => Promise.resolve({ data: null, error: null }),
        csv: () => Promise.resolve({ data: null, error: null }),
        then: (resolve) => resolve({ data: [], error: null }) // Make it awaitable
    };
    return builder;
};

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase credentials missing! Using a dummy client.');
  client = {
      from: () => createMockQueryBuilder(),
      auth: {
          getSession: () => Promise.resolve({ data: { session: { user: { id: "1" } } } }),
          getUser: () => Promise.resolve({ data: { user: { id: "1" } } }),
          onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
          signOut: () => Promise.resolve({ error: null }),
          signInWithPassword: () => Promise.resolve({ data: { session: { user: { id: "1" } }, user: { id: "1" } }, error: null })
      },
      functions: { invoke: () => Promise.resolve({ data: { response: "This is a mock question?" }, error: null }) },
      rpc: () => {
        const p = Promise.resolve({ data: null, error: null });
        p.catch = (cb) => p.then(null, cb);
        return p;
      },
      removeChannel: () => {},
      channel: () => { const ch = { on: () => ch, track: () => Promise.resolve(), presenceState: () => ({}), subscribe: (cb) => { if(cb) cb("SUBSCRIBED"); return { unsubscribe: () => {} }; } }; return ch; },
      _old_channel: () => ({
          on: () => ({
              subscribe: (cb) => { if(cb) cb('SUBSCRIBED'); return { unsubscribe: () => {} }; }
          }),
          subscribe: (cb) => { if(cb) cb('SUBSCRIBED'); return { unsubscribe: () => {} }; }
      }),
      storage: {
          from: () => ({
              upload: () => Promise.resolve({ data: null, error: null }),
              getPublicUrl: () => ({ data: { publicUrl: '' } }),
              download: () => Promise.resolve({ data: new Blob(), error: null })
          })
      }
  };
} else {
    try {
        client = createClient(supabaseUrl, supabaseAnonKey);
    } catch (e) {
        console.error("Supabase init failed:", e);
        client = {
            from: () => createMockQueryBuilder(),
            auth: {
                getSession: () => Promise.resolve({ data: { session: { user: { id: "1" } } } }),
                onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } })
            },
            functions: { invoke: () => Promise.resolve({ data: { response: "This is a mock question?" }, error: null }) },
      rpc: () => {
        const p = Promise.resolve({ data: null, error: null });
        p.catch = (cb) => p.then(null, cb);
        return p;
      },
      removeChannel: () => {},
            channel: () => { const ch = { on: () => ch, track: () => Promise.resolve(), presenceState: () => ({}), subscribe: (cb) => { if(cb) cb("SUBSCRIBED"); return { unsubscribe: () => {} }; } }; return ch; },
            storage: { from: () => ({ upload: () => Promise.resolve({}), getPublicUrl: () => ({ data: { publicUrl: '' } }) }) }
        };
    }
}

export const supabase = client;
